import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppContext } from "@/context/context";
import { usePortfolioAndAccountSelection } from "@/utils/hooks/usePortfolioAndAccountSelection/usePortfolioAndAccountSelection";

import type { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import type { ContextStateType } from "@/context/context.types";

/**
 * Hook to manage account and portfolio selection.
 *
 * Integrates with:
 * - Global AppContext for org metadata and selected account/portfolio
 * - URL params (accounts) for initial selection
 * - usePortfolioAndAccountSelection utility for complex selection logic
 *
 * Provides:
 * - Selected account and portfolio objects
 * - Dropdown options for portfolios and accounts
 * - Change handlers that update both context and URL
 */
export const useAccountSelection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    context_state,
    set_portfolio_by_id,
    set_account_by_id,
  } = useAppContext();

  const [initializing, setInitializing] = useState(true);

  // Get account ID from URL param
  const accountIdParam = searchParams.get("accounts");

  // Get local storage context
  const stringifiedLocalMetadata = localStorage.getItem("pulse-ctx");
  const localMetadata: ContextStateType | null = stringifiedLocalMetadata
    ? JSON.parse(stringifiedLocalMetadata)
    : null;

  // Initialize account selection on mount
  useEffect(() => {
    const initializeSelection = async () => {
      if (!context_state.org_metadata?.portfolios || context_state.org_metadata.portfolios.length === 0) {
        return;
      }

      try {
        const { portfolio_id_res, account_id_res } = await usePortfolioAndAccountSelection(
          accountIdParam,
          localMetadata,
          context_state.org_metadata,
          false // all_accounts_enabled - Blotter doesn't support "all accounts"
        );

        // Set portfolio if different from current
        if (portfolio_id_res && portfolio_id_res !== context_state.selected_portfolio?.portfolio_id) {
          await set_portfolio_by_id(portfolio_id_res);
        }

        // Set account if different from current
        if (account_id_res && account_id_res !== context_state.selected_account?.account_id) {
          await set_account_by_id(account_id_res);
        }

        // Update URL with selected account
        if (account_id_res && account_id_res !== accountIdParam) {
          setSearchParams((prev) => {
            prev.set("accounts", account_id_res);
            return prev;
          }, { replace: true });
        }
      } catch (error) {
        console.error("Error initializing account selection:", error);
      } finally {
        setInitializing(false);
      }
    };

    if (initializing) {
      initializeSelection();
    }
  }, [initializing, accountIdParam, localMetadata, context_state.org_metadata, context_state.selected_portfolio, context_state.selected_account, set_portfolio_by_id, set_account_by_id, setSearchParams]);

  // Map portfolios to select options
  const portfolioOptions = useMemo((): ISelectOption[] => {
    if (!context_state.org_metadata?.portfolios) return [];

    return context_state.org_metadata.portfolios.map((portfolio) => ({
      label: portfolio.display_name,
      value: portfolio.portfolio_id,
    }));
  }, [context_state.org_metadata?.portfolios]);

  // Map accounts to select options (from selected portfolio)
  const accountOptions = useMemo((): ISelectOption[] => {
    if (!context_state.selected_portfolio?.accounts) return [];

    return context_state.selected_portfolio.accounts.map((account) => ({
      label: account.display_name,
      value: account.account_id,
    }));
  }, [context_state.selected_portfolio?.accounts]);

  // Handle portfolio dropdown change
  const handlePortfolioChange = useCallback(async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const portfolioId = e.target.value;

    if (!portfolioId) return;

    try {
      // Update context
      await set_portfolio_by_id(portfolioId);

      // Reset account to first in portfolio
      const portfolio = context_state.org_metadata.portfolios.find(p => p.portfolio_id === portfolioId);
      if (portfolio?.accounts && portfolio.accounts.length > 0) {
        const firstAccountId = portfolio.accounts[0].account_id;
        await set_account_by_id(firstAccountId);

        // Update URL
        setSearchParams((prev) => {
          prev.set("accounts", firstAccountId);
          return prev;
        }, { replace: true });
      }
    } catch (error) {
      console.error("Error changing portfolio:", error);
    }
  }, [context_state.org_metadata.portfolios, set_portfolio_by_id, set_account_by_id, setSearchParams]);

  // Handle account dropdown change
  const handleAccountChange = useCallback(async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accountId = e.target.value;

    if (!accountId) return;

    try {
      // Update context
      await set_account_by_id(accountId);

      // Update URL
      setSearchParams((prev) => {
        prev.set("accounts", accountId);
        return prev;
      }, { replace: true });
    } catch (error) {
      console.error("Error changing account:", error);
    }
  }, [set_account_by_id, setSearchParams]);

  return {
    selectedAccount: context_state.selected_account,
    selectedPortfolio: context_state.selected_portfolio,
    accountOptions,
    portfolioOptions,
    handlePortfolioChange,
    handleAccountChange,
    initializing,
  };
};
